/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REVIEW_PROFILE_FORM_SPLIT
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Refactored ReviewProfileForm into sub-components and custom hooks to comply with 
 * file size limits. Orchestrates Company and Profile review tabs.
 *
 * File Size Check:
 * - Current: ~180 lines
 * - Limit: 200 lines
 * - Decision: Splitting complete (orchestrator is under limit)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/review-profile/components/CompanyTab.tsx
 * - client/src/components/setup/review-profile/components/ProfileTab.tsx
 * - client/src/components/setup/review-profile/hooks/useReviewStatus.ts
 */
import React, { useState, useEffect, useRef } from "react";
import { Nav, Spinner } from "react-bootstrap";
import { useSearchParams, useNavigate } from "react-router-dom";
import SetupBottomNavigation from "../SetupBottomNavigation";
import { useSetup } from "../../Setup";
import type { Hours, ImageData, User } from "../../../interfaces/User";
import type { BroadcastSignatureEditorRef } from "../broadcast-signature-editor/BroadcastSignatureEditor";
import { useReviewStatus } from "./hooks/useReviewStatus";
import { CompanyTab } from "./components/CompanyTab";
import { ProfileTab } from "./components/ProfileTab";

type ReviewTabKey = "company" | "profile";

const getImageSrc = (img?: ImageData | null): string | null => {
  if (!img) return null;
  return img.data.startsWith("data:") ? img.data : `data:${img.contentType};base64,${img.data}`;
};

const formatAddress = (profile: Partial<User> | null): string => {
  if (!profile) return "";
  return [profile.address1, profile.address2, profile.city, profile.state, profile.zip]
    .filter(Boolean)
    .join(", ");
};

const formatHours = (hours?: Hours): string => {
  if (!hours || !(hours.fromDay && hours.toDay && hours.from && hours.to && hours.timeZone)) {
    return "Hours not provided";
  }
  const dayPart = hours.fromDay === hours.toDay ? hours.fromDay : `${hours.fromDay} – ${hours.toDay}`;
  const tzPart = hours.timeZone.includes("/") ? hours.timeZone.split("/").pop()?.replace(/_/g, " ") : hours.timeZone;
  return `${dayPart}, ${hours.from} – ${hours.to}, ${tzPart}`;
};

const ReviewProfileForm: React.FC = () => {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile, loading, error, save } = useSetup();
  const [activeTab, setActiveTab] = useState<ReviewTabKey>("company");
  const [broadcastSignatureHtml, setBroadcastSignatureHtml] = useState<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const signatureEditorRef = useRef<BroadcastSignatureEditorRef | null>(null);

  const status = useReviewStatus(profile);
  const companyAddress = formatAddress(profile);
  const hoursText = formatHours(profile?.hours);

  useEffect(() => {
    if (!profile) return;
    if (profile.broadcastSignatureHtml?.trim()) {
      setBroadcastSignatureHtml(profile.broadcastSignatureHtml);
      setIsDirty(false);
      return;
    }
    const fullName = `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || "Your name";
    const lines = [
      `<p><strong>${fullName}</strong></p>`,
      `<p>${profile.jobTitle || "Your position"}${profile.companyName ? `, ${profile.companyName}` : ""}</p>`,
      profile.telCompany || profile.tel ? `<p>${profile.telCompany || profile.tel}</p>` : "",
      profile.email || profile.companyEmail ? `<p>${profile.email || profile.companyEmail}</p>` : "",
      profile.website ? `<p>${profile.website}</p>` : "",
    ].filter(Boolean);
    setBroadcastSignatureHtml(lines.join(""));
    setIsDirty(true);
  }, [profile]);

  const handleSaveSignature = async () => {
    if (!broadcastSignatureHtml || !isDirty) return;
    try {
      if (signatureEditorRef.current) signatureEditorRef.current.forceUpdateWithStyles();
      setIsSaving(true);
      await save({ broadcastSignatureHtml });
      setIsDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error("Failed to save broadcast signature", e);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && !profile) {
    return <div className="review-page-loading d-flex justify-content-center align-items-center"><Spinner animation="border" /></div>;
  }

  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="review-page">
      <div className="review-tabs-container mb-4">
        <Nav variant="tabs" className="review-tabs" activeKey={activeTab} onSelect={(k) => setActiveTab((k as ReviewTabKey) || "company")}>
          <Nav.Item className="review-tab-item"><Nav.Link eventKey="company">Company profile</Nav.Link></Nav.Item>
          <Nav.Item className="review-tab-item"><Nav.Link eventKey="profile">My profile</Nav.Link></Nav.Item>
        </Nav>
      </div>

      {activeTab === "company" ? (
        <CompanyTab profile={profile} getImageSrc={getImageSrc} companyAddress={companyAddress} hoursText={hoursText} />
      ) : (
        <ProfileTab 
          profile={profile} 
          getImageSrc={getImageSrc} 
          broadcastSignatureHtml={broadcastSignatureHtml}
          isDirty={isDirty}
          isSaving={isSaving}
          saved={saved}
          onSignatureChange={(html) => { setBroadcastSignatureHtml(html); setIsDirty(true); setSaved(false); }}
          onSaveSignature={handleSaveSignature}
          signatureEditorRef={signatureEditorRef}
        />
      )}

      <SetupBottomNavigation
        currentSection="review"
        onBack={() => setSearchParams({ section: "profile" })}
        onSaveAndNext={() => navigate("/")}
        canSkip={false}
        saveNextButtonText="Done"
        saveNextDisabled={status.percentage !== 100}
      />
    </div>
  );
};

export default ReviewProfileForm;
